"use client";

import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { ChevronRight } from "lucide-react";

import type { HelpArticleSummary } from "@/lib/help-api";

export function HelpArticleList({
  articles,
  emptyKey = "noArticlesInCategory",
  emptyVars,
}: {
  articles: HelpArticleSummary[];
  emptyKey?:
    | "noArticlesInCategory"
    | "noFeatured"
    | "noResultsFor"
    | "searchPromptHint";
  emptyVars?: Record<string, string>;
}) {
  const locale = useLocale();
  const t = useTranslations("help");
  const emptyLabel = t(emptyKey, emptyVars);

  if (articles.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-neutral-200 p-6 text-center text-sm text-muted dark:border-neutral-800 dark:text-muted-dark">
        {emptyLabel}
      </div>
    );
  }

  return (
    <ul className="divide-y divide-neutral-100 rounded-md border border-neutral-200 bg-white dark:divide-neutral-800 dark:border-neutral-800 dark:bg-neutral-950">
      {articles.map((article) => (
        <li key={article.id}>
          <Link
            href={`/${locale}/help/article/${article.slug}`}
            className="flex items-start justify-between gap-4 px-4 py-3 transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-900/60"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-neutral-900 dark:text-white">
                {article.title}
              </div>
              {article.excerpt && (
                <div className="mt-0.5 line-clamp-1 text-xs text-muted dark:text-muted-dark">
                  {article.excerpt}
                </div>
              )}
            </div>
            <ChevronRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-neutral-400 dark:text-neutral-600" />
          </Link>
        </li>
      ))}
    </ul>
  );
}
